import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Lightning, Clock, ShieldCheck, CheckCircle, XCircle,
  Timer, CaretRight, CaretLeft, SpinnerGap, MagnifyingGlass,
  Warning, ArrowClockwise, Prohibit, CalendarBlank, Check, X
} from '@phosphor-icons/react';
import InfoTooltip from '../components/InfoTooltip';
import EmptyState from '../components/EmptyState';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  active: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-blue-100 text-blue-700',
  expired: 'bg-slate-100 text-navy-600',
  revoked: 'bg-red-100 text-red-700',
  rejected: 'bg-red-100 text-red-700',
};

const DURATION_OPTIONS = [
  { value: 0.5, label: '30 min' },
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 12, label: '12 hours' },
];

function formatTimeRemaining(expiresAt) {
  if (!expiresAt) return '';
  const now = new Date();
  const exp = new Date(expiresAt);
  const diff = exp - now;
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function CountdownTimer({ expiresAt }) {
  const [remaining, setRemaining] = useState(formatTimeRemaining(expiresAt));
  useEffect(() => {
    const interval = setInterval(() => setRemaining(formatTimeRemaining(expiresAt)), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  const isLow = remaining.startsWith('0') || remaining === 'Expired' ||
    (remaining.includes('m') && !remaining.includes('h') && parseInt(remaining) < 15);
  return <span className={`font-mono text-sm font-bold ${isLow ? 'text-red-600' : 'text-emerald-600'}`}>{remaining}</span>;
}

function WizardStepIndicator({ currentStep }) {
  const steps = ['Select Role', 'Schedule', 'Review & Submit'];
  return (
    <div className="flex items-center justify-center gap-2 mb-6" data-testid="jit-wizard-steps">
      {steps.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = stepNum === currentStep;
        const isCompleted = stepNum < currentStep;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                isCompleted ? 'bg-emerald-500 text-white' :
                isActive ? 'bg-orange-500 text-white' :
                'bg-slate-200 text-navy-500'
              }`}>
                {isCompleted ? <Check size={14} weight="bold" /> : stepNum}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-orange-600' : isCompleted ? 'text-emerald-600' : 'text-navy-400'}`}>
                {label}
              </span>
            </div>
            {idx < steps.length - 1 && <div className={`w-8 h-0.5 ${isCompleted ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function JitAccessPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('request');
  const [roles, setRoles] = useState([]);
  const [roleSearch, setRoleSearch] = useState('');
  const [jitRequests, setJitRequests] = useState([]);
  const [myActive, setMyActive] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [canRequest, setCanRequest] = useState(null);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);
  const [duration, setDuration] = useState(1);
  const [startMode, setStartMode] = useState('immediate'); // 'immediate' | 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // History filters
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  const isAdmin = user?.admin_role === 'admin' || user?.admin_role === 'governance_admin';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, activeRes, canRes] = await Promise.all([
        API.get('/jit/stats'),
        API.get('/jit/requests/my-active'),
        API.get('/jit/can-request'),
      ]);
      setStats(statsRes.data);
      setMyActive(activeRes.data.items);
      setCanRequest(canRes.data.allowed);
    } catch (err) { void 0; }
    finally { setLoading(false); }
  }, []);

  const loadRequests = useCallback(async (status, searchOpts) => {
    try {
      const params = {};
      if (status) params.status = status;
      if (searchOpts?.search) params.search = searchOpts.search;
      if (searchOpts?.date_from) params.date_from = searchOpts.date_from;
      if (searchOpts?.date_to) params.date_to = searchOpts.date_to;
      if (searchOpts?.filterStatus) params.status = searchOpts.filterStatus;
      const res = await API.get('/jit/requests', { params });
      setJitRequests(res.data.items);
    } catch (err) { void 0; }
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      const res = await API.get('/jit/privileged-roles');
      setRoles(res.data.items || []);
    } catch (err) { void 0; }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (tab === 'request') loadRoles();
    else if (tab === 'pending') loadRequests('pending');
    else if (tab === 'history') loadRequests(null, { search: historySearch, filterStatus: historyStatus, date_from: historyDateFrom, date_to: historyDateTo });
    else if (tab === 'active') { loadData(); loadRequests('active'); }
  }, [tab, loadRoles, loadRequests, loadData, historySearch, historyStatus, historyDateFrom, historyDateTo]);

  const resetWizard = () => {
    setWizardStep(1);
    setSelectedRole(null);
    setDuration(1);
    setStartMode('immediate');
    setScheduledDate('');
    setScheduledTime('');
    setJustification('');
  };

  const handleSubmit = async () => {
    if (!selectedRole || !justification.trim()) {
      toast.error('Please select a role and provide a justification');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        role_id: selectedRole.id,
        role_name: selectedRole.name,
        duration_hours: duration,
        justification: justification.trim(),
      };
      if (startMode === 'scheduled' && scheduledDate && scheduledTime) {
        payload.scheduled_start = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }
      await API.post('/jit/requests', payload);
      toast.success(startMode === 'scheduled'
        ? `Scheduled JIT access request submitted for ${scheduledDate} ${scheduledTime}`
        : 'JIT access request submitted for approval');
      resetWizard();
      await loadData();
      setTab('active');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally { setSubmitting(false); }
  };

  const handleApprove = async (id) => {
    try { await API.put(`/jit/requests/${id}/approve`); toast.success('JIT access approved'); loadRequests('pending'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };
  const handleReject = async (id) => {
    try { await API.put(`/jit/requests/${id}/reject`, { reason: '' }); toast.success('JIT access rejected'); loadRequests('pending'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };
  const handleRevoke = async (id) => {
    try { await API.put(`/jit/requests/${id}/revoke`); toast.success('JIT access revoked'); loadData(); loadRequests('active'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to revoke'); }
  };

  const filteredRoles = roles.filter(r =>
    r.name?.toLowerCase().includes(roleSearch.toLowerCase()) ||
    r.description?.toLowerCase().includes(roleSearch.toLowerCase())
  );

  // Minimum date/time for scheduling (now + 5 min)
  const now = new Date();
  const minDate = now.toISOString().slice(0, 10);

  const getScheduledSummary = () => {
    if (startMode === 'immediate') return 'Starts immediately upon approval';
    if (scheduledDate && scheduledTime) {
      const dt = new Date(`${scheduledDate}T${scheduledTime}`);
      return `Scheduled: ${dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} at ${dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
    }
    return 'Select a date and time';
  };

  const canProceedStep2 = !!selectedRole;
  const canProceedStep3 = duration > 0 && (startMode === 'immediate' || (scheduledDate && scheduledTime));
  const canSubmit = canProceedStep2 && canProceedStep3 && justification.trim().length > 0;

  const tabs = [
    { id: 'request', label: 'Request Access', icon: Lightning },
    { id: 'active', label: 'My Sessions', icon: Timer, count: myActive.length },
    ...(isAdmin ? [{ id: 'pending', label: 'Pending Approvals', icon: Clock, count: stats.pending }] : []),
    { id: 'history', label: 'History', icon: ArrowClockwise },
  ];

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="jit-page-title">Privileged Access <InfoTooltip concept="jit_access" /></h1>
          <p className="text-sm text-navy-500 mt-1">Just-in-Time access to privileged roles — max 12 hours, auto-revoked on expiry</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Pending', value: stats.pending || 0, color: 'bg-orange-500' },
          { label: 'Active', value: stats.active || 0, color: 'bg-orange-500' },
          { label: 'Expired', value: stats.expired || 0, color: 'bg-navy-600' },
          { label: 'Revoked', value: stats.revoked || 0, color: 'bg-navy-600' },
          { label: 'Rejected', value: stats.rejected || 0, color: 'bg-navy-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid={`jit-stat-${s.label.toLowerCase()}`}>
            <div className={`h-1.5 ${s.color}`} />
            <div className="p-4 text-center">
              <p className="text-2xl font-bold text-navy-900">{s.value}</p>
              <p className="text-[10px] text-navy-500 uppercase tracking-widest mt-1">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1">
        {tabs.map(t => (
          <button key={t.id} data-testid={`jit-tab-${t.id}`} onClick={() => { setTab(t.id); if (t.id === 'request') resetWizard(); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-navy-500 hover:text-navy-700'
            }`}>
            <t.icon size={16} weight={tab === t.id ? 'fill' : 'regular'} />
            {t.label}
            {t.count > 0 && <Badge className="bg-orange-100 text-orange-700 text-[10px] ml-1">{t.count}</Badge>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loading && tab !== 'request' ? (
        <div className="flex justify-center py-16"><SpinnerGap size={32} className="animate-spin text-navy-400" /></div>
      ) : (
        <>
          {/* REQUEST WIZARD */}
          {tab === 'request' && (
            canRequest === false ? (
              <div className="bg-white border border-slate-200 rounded-sm p-10 text-center" data-testid="jit-no-permission">
                <Prohibit size={48} className="text-navy-300 mx-auto" />
                <h3 className="text-lg font-semibold text-navy-700 mt-4">Access Restricted</h3>
                <p className="text-sm text-navy-500 mt-2 max-w-md mx-auto">
                  You need the <strong>"Privileged Access"</strong> admin role to request Just-in-Time privileged access.
                </p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="jit-request-wizard">
                <div className="h-1.5 bg-orange-500" />
                <div className="p-6">
                  <WizardStepIndicator currentStep={wizardStep} />

                  {/* STEP 1: Select Role */}
                  {wizardStep === 1 && (
                    <div data-testid="jit-wizard-step-1">
                      <h3 className="text-sm font-bold text-navy-900 uppercase tracking-widest mb-3">Step 1 — Select a Privileged Role</h3>
                      <div className="relative mb-3">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                        <Input data-testid="jit-role-search-input" placeholder="Search roles..." value={roleSearch}
                          onChange={(e) => setRoleSearch(e.target.value)} className="pl-9" />
                      </div>
                      <div className="border border-slate-200 rounded-sm max-h-[320px] overflow-y-auto divide-y divide-slate-100">
                        {filteredRoles.length === 0 ? (
                          <p className="text-sm text-navy-400 p-5 text-center">No privileged roles found</p>
                        ) : filteredRoles.map(role => (
                          <button key={role.id} data-testid={`jit-role-${role.id}`}
                            onClick={() => setSelectedRole(role)}
                            className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors ${
                              selectedRole?.id === role.id ? 'bg-orange-50 border-l-2 border-orange-500' : ''
                            }`}>
                            <div>
                              <p className="text-sm font-medium text-navy-900">{role.name}</p>
                              <p className="text-xs text-navy-500 mt-0.5">{role.description || 'No description'}</p>
                              <Badge className="text-[10px] bg-slate-100 text-navy-600 mt-1">{(role.entitlements || []).length} entitlements</Badge>
                            </div>
                            {selectedRole?.id === role.id && <CheckCircle size={20} weight="fill" className="text-orange-500 shrink-0" />}
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-end mt-4">
                        <Button onClick={() => setWizardStep(2)} disabled={!canProceedStep2}
                          className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="jit-wizard-next-1">
                          Next: Schedule <CaretRight size={14} className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Schedule */}
                  {wizardStep === 2 && (
                    <div data-testid="jit-wizard-step-2">
                      <h3 className="text-sm font-bold text-navy-900 uppercase tracking-widest mb-4">Step 2 — Schedule Access</h3>

                      {/* Selected role summary */}
                      <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 mb-5 flex items-center gap-2">
                        <ShieldCheck size={18} weight="duotone" className="text-orange-600 shrink-0" />
                        <span className="text-sm font-semibold text-orange-800">{selectedRole?.name}</span>
                      </div>

                      {/* Duration */}
                      <label className="block text-xs font-bold text-navy-700 uppercase tracking-widest mb-2">How long do you need access?</label>
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
                        {DURATION_OPTIONS.map(opt => (
                          <button key={opt.value} data-testid={`jit-duration-${opt.value}`}
                            onClick={() => setDuration(opt.value)}
                            className={`px-3 py-2.5 rounded-sm text-xs font-medium border transition-colors ${
                              duration === opt.value
                                ? 'bg-orange-500 text-white border-orange-500'
                                : 'bg-white text-navy-700 border-slate-200 hover:border-orange-300'
                            }`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {/* Start Mode Toggle */}
                      <label className="block text-xs font-bold text-navy-700 uppercase tracking-widest mb-2">When should access start?</label>
                      <div className="flex gap-2 mb-4">
                        <button data-testid="jit-start-immediate" onClick={() => setStartMode('immediate')}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm border text-sm font-medium transition-colors ${
                            startMode === 'immediate' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-navy-700 border-slate-200 hover:border-orange-300'
                          }`}>
                          <Lightning size={16} weight={startMode === 'immediate' ? 'fill' : 'regular'} />
                          Immediately
                        </button>
                        <button data-testid="jit-start-scheduled" onClick={() => setStartMode('scheduled')}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm border text-sm font-medium transition-colors ${
                            startMode === 'scheduled' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-navy-700 border-slate-200 hover:border-orange-300'
                          }`}>
                          <CalendarBlank size={16} weight={startMode === 'scheduled' ? 'fill' : 'regular'} />
                          Schedule for Later
                        </button>
                      </div>

                      {/* Scheduled Date/Time Picker */}
                      {startMode === 'scheduled' && (
                        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-4 space-y-3" data-testid="jit-schedule-picker">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-semibold text-navy-600 uppercase tracking-widest mb-1">Start Date</label>
                              <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
                                min={minDate} className="text-sm" data-testid="jit-schedule-date" />
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-navy-600 uppercase tracking-widest mb-1">Start Time</label>
                              <Input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)}
                                className="text-sm" data-testid="jit-schedule-time" />
                            </div>
                          </div>
                          {scheduledDate && scheduledTime && (
                            <p className="text-xs text-navy-500 flex items-center gap-1">
                              <CalendarBlank size={12} className="text-orange-500" />
                              Access will activate on <strong className="text-navy-700">{new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}</strong> and expire after <strong className="text-navy-700">{duration}h</strong>
                            </p>
                          )}
                        </div>
                      )}

                      {/* Warning */}
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                        <Warning size={16} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700">
                          Access will be <strong>automatically revoked</strong> after {duration} hour{duration !== 1 ? 's' : ''}. All actions during the session are audited.
                        </p>
                      </div>

                      <div className="flex justify-between mt-4">
                        <Button variant="outline" onClick={() => setWizardStep(1)} className="rounded-sm" data-testid="jit-wizard-back-2">
                          <CaretLeft size={14} className="mr-1" /> Back
                        </Button>
                        <Button onClick={() => setWizardStep(3)} disabled={!canProceedStep3}
                          className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="jit-wizard-next-2">
                          Next: Review <CaretRight size={14} className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Review & Submit */}
                  {wizardStep === 3 && (
                    <div data-testid="jit-wizard-step-3">
                      <h3 className="text-sm font-bold text-navy-900 uppercase tracking-widest mb-4">Step 3 — Review & Submit</h3>

                      {/* Summary Card */}
                      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-navy-500 uppercase tracking-widest font-semibold">Role</span>
                          <span className="text-sm font-semibold text-navy-900 flex items-center gap-1.5">
                            <ShieldCheck size={14} className="text-orange-500" /> {selectedRole?.name}
                          </span>
                        </div>
                        <div className="border-t border-slate-200" />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-navy-500 uppercase tracking-widest font-semibold">Duration</span>
                          <span className="text-sm font-medium text-navy-900"><Timer size={14} className="inline mr-1 text-navy-400" />{duration} hour{duration !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="border-t border-slate-200" />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-navy-500 uppercase tracking-widest font-semibold">Start</span>
                          <span className="text-sm font-medium text-navy-900 flex items-center gap-1">
                            {startMode === 'immediate' ? (
                              <><Lightning size={14} className="text-orange-500" /> Immediately on approval</>
                            ) : (
                              <><CalendarBlank size={14} className="text-orange-500" /> {getScheduledSummary()}</>
                            )}
                          </span>
                        </div>
                        <div className="border-t border-slate-200" />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-navy-500 uppercase tracking-widest font-semibold">Auto-Revokes</span>
                          <span className="text-sm font-medium text-navy-900">
                            {startMode === 'immediate'
                              ? `${duration}h after approval`
                              : scheduledDate && scheduledTime
                                ? new Date(new Date(`${scheduledDate}T${scheduledTime}`).getTime() + duration * 3600000).toLocaleString()
                                : '-'}
                          </span>
                        </div>
                      </div>

                      {/* Justification */}
                      <label className="block text-xs font-bold text-navy-700 uppercase tracking-widest mb-2">Justification (required)</label>
                      <textarea data-testid="jit-justification" value={justification} onChange={(e) => setJustification(e.target.value)}
                        placeholder="Why do you need this privileged access? Describe the task or incident."
                        className="w-full border border-slate-200 rounded-sm p-3 text-sm h-24 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none" />

                      <div className="flex justify-between mt-5">
                        <Button variant="outline" onClick={() => setWizardStep(2)} className="rounded-sm" data-testid="jit-wizard-back-3">
                          <CaretLeft size={14} className="mr-1" /> Back
                        </Button>
                        <Button data-testid="jit-submit-btn" onClick={handleSubmit} disabled={!canSubmit || submitting}
                          className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6">
                          {submitting ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <Lightning size={16} weight="fill" className="mr-2" />}
                          Submit Request
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          {/* ACTIVE SESSIONS TAB */}
          {tab === 'active' && (
            <div className="space-y-3" data-testid="jit-my-sessions">
              {myActive.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-10 text-center">
                  <Timer size={40} className="text-navy-300 mx-auto" />
                  <p className="text-sm text-navy-500 mt-3">No active, pending, or scheduled sessions</p>
                  <Button variant="outline" className="mt-3" onClick={() => setTab('request')}>Request Access</Button>
                </div>
              ) : myActive.map(req => {
                const isPending = req.status === 'pending';
                const isScheduled = req.status === 'scheduled';
                const isActive = req.status === 'active';
                const borderColor = isPending ? 'border-amber-200' : isScheduled ? 'border-blue-200' : 'border-emerald-200';
                const iconBg = isPending ? 'bg-amber-100' : isScheduled ? 'bg-blue-100' : 'bg-emerald-100';
                const iconColor = isPending ? 'text-amber-600' : isScheduled ? 'text-blue-600' : 'text-emerald-600';
                const StatusIcon = isPending ? Clock : isScheduled ? CalendarBlank : Lightning;

                return (
                  <div key={req.id} data-testid={`jit-session-${req.id}`} className={`bg-white border ${borderColor} rounded-sm p-5`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center shrink-0`}>
                          <StatusIcon size={20} weight="fill" className={iconColor} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-navy-900">{req.role_name}</p>
                            <Badge className={`text-[10px] uppercase ${STATUS_STYLES[req.status]}`}>{req.status}</Badge>
                          </div>
                          <p className="text-xs text-navy-500 mt-0.5">{req.duration_hours}h session</p>
                        </div>
                      </div>

                      <div className="text-right flex flex-col items-end gap-2 shrink-0">
                        {isActive && (
                          <>
                            <div className="flex items-center gap-2"><Timer size={16} className="text-emerald-600" /><CountdownTimer expiresAt={req.expires_at} /></div>
                            <Button data-testid={`jit-revoke-${req.id}`} variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleRevoke(req.id)}>
                              <Prohibit size={14} className="mr-1" /> Revoke Early
                            </Button>
                          </>
                        )}
                        {isPending && (
                          <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                            <Clock size={14} /> Awaiting approval
                          </span>
                        )}
                        {isScheduled && (
                          <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                            <CalendarBlank size={14} /> Approved — starts later
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-navy-500 flex-wrap">
                      {isPending && (
                        <>
                          <span>Submitted: {new Date(req.created_at).toLocaleString()}</span>
                          {req.scheduled_start && (
                            <span className="flex items-center gap-1 text-blue-600 font-medium">
                              <CalendarBlank size={12} /> Scheduled start: {new Date(req.scheduled_start).toLocaleString()}
                            </span>
                          )}
                        </>
                      )}
                      {isScheduled && (
                        <>
                          <span>Approved by: <strong className="text-navy-700">{req.approved_by_name}</strong></span>
                          <span className="flex items-center gap-1 text-blue-600 font-medium">
                            <CalendarBlank size={12} /> Starts: {new Date(req.activated_at || req.scheduled_start).toLocaleString()}
                          </span>
                          <span>Expires: {new Date(req.expires_at).toLocaleString()}</span>
                        </>
                      )}
                      {isActive && (
                        <>
                          <span>Approved by: <strong className="text-navy-700">{req.approved_by_name}</strong></span>
                          <span>Activated: {new Date(req.activated_at).toLocaleString()}</span>
                          <span>Expires: {new Date(req.expires_at).toLocaleString()}</span>
                        </>
                      )}
                    </div>

                    {req.justification && (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <p className="text-xs text-navy-400">Justification: <span className="text-navy-600">{req.justification}</span></p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* PENDING APPROVALS TAB */}
          {tab === 'pending' && (
            <div className="space-y-3">
              {jitRequests.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-10 text-center">
                  <CheckCircle size={40} className="text-navy-300 mx-auto" /><p className="text-sm text-navy-500 mt-3">No pending JIT requests</p>
                </div>
              ) : jitRequests.map(req => (
                <div key={req.id} data-testid={`jit-pending-${req.id}`} className="bg-white border border-amber-200 rounded-sm p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Clock size={20} weight="fill" className="text-amber-600" /></div>
                      <div>
                        <p className="font-semibold text-navy-900">{req.requester_name}</p>
                        <p className="text-xs text-navy-500">
                          Requesting <strong className="text-navy-700">{req.role_name}</strong> for <strong className="text-navy-700">{req.duration_hours}h</strong>
                        </p>
                        {req.scheduled_start && (
                          <p className="text-xs text-blue-600 mt-0.5 flex items-center gap-1">
                            <CalendarBlank size={12} /> Scheduled: {new Date(req.scheduled_start).toLocaleString()}
                          </p>
                        )}
                        <p className="text-xs text-navy-400 mt-1">{new Date(req.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button data-testid={`jit-approve-${req.id}`} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => handleApprove(req.id)}>
                        <CheckCircle size={14} className="mr-1" /> Approve
                      </Button>
                      <Button data-testid={`jit-reject-${req.id}`} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleReject(req.id)}>
                        <XCircle size={14} className="mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                  {req.justification && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-navy-500 uppercase tracking-widest mb-1">Justification</p>
                      <p className="text-sm text-navy-700 bg-slate-50 p-2 rounded">{req.justification}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* HISTORY TAB */}
          {tab === 'history' && (
            <div className="space-y-4" data-testid="jit-history-section">
              {/* Search & Filter Bar */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                  <Input data-testid="jit-history-search" placeholder="Search by name or role..."
                    value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} className="pl-9 h-9 text-sm rounded-sm" />
                </div>

                {/* Status Filter Pills */}
                <div className="flex gap-1.5" data-testid="jit-history-status-filters">
                  {[
                    { value: '', label: 'All' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'active', label: 'Active' },
                    { value: 'scheduled', label: 'Scheduled' },
                    { value: 'expired', label: 'Expired' },
                    { value: 'revoked', label: 'Revoked' },
                    { value: 'rejected', label: 'Rejected' },
                  ].map(f => (
                    <button key={f.value} data-testid={`jit-history-filter-${f.value || 'all'}`}
                      onClick={() => setHistoryStatus(f.value)}
                      className={`px-2.5 py-1 rounded-sm text-xs font-medium border transition-colors ${
                        historyStatus === f.value
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-white text-navy-600 border-slate-200 hover:border-orange-300'
                      }`}>
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Date Range */}
                <div className="flex items-center gap-2">
                  <Input type="date" data-testid="jit-history-date-from" value={historyDateFrom}
                    onChange={(e) => setHistoryDateFrom(e.target.value)} className="h-9 text-xs rounded-sm w-[140px]" />
                  <span className="text-xs text-navy-400">to</span>
                  <Input type="date" data-testid="jit-history-date-to" value={historyDateTo}
                    onChange={(e) => setHistoryDateTo(e.target.value)} className="h-9 text-xs rounded-sm w-[140px]" />
                  {(historyDateFrom || historyDateTo) && (
                    <button data-testid="jit-history-clear-dates" onClick={() => { setHistoryDateFrom(''); setHistoryDateTo(''); }}
                      className="p-1.5 rounded hover:bg-slate-100 text-navy-400"><X size={14} /></button>
                  )}
                </div>
              </div>

              {/* Results count */}
              <p className="text-xs text-navy-400">{jitRequests.length} result{jitRequests.length !== 1 ? 's' : ''}</p>

              {/* Table */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
                <div className="h-1.5 bg-navy-600" />
                <table className="w-full text-sm" data-testid="jit-history-table">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-4 py-3 text-[10px] font-bold text-navy-500 uppercase tracking-widest">Requester</th>
                      <th className="text-left px-4 py-3 text-[10px] font-bold text-navy-500 uppercase tracking-widest">Role</th>
                      <th className="text-left px-4 py-3 text-[10px] font-bold text-navy-500 uppercase tracking-widest">Duration</th>
                      <th className="text-left px-4 py-3 text-[10px] font-bold text-navy-500 uppercase tracking-widest">Status</th>
                      <th className="text-left px-4 py-3 text-[10px] font-bold text-navy-500 uppercase tracking-widest">Requested</th>
                      <th className="text-left px-4 py-3 text-[10px] font-bold text-navy-500 uppercase tracking-widest">Decided By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {jitRequests.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-sm text-navy-400">
                        <MagnifyingGlass size={28} className="mx-auto mb-2 text-navy-300" />
                        No JIT requests match your filters
                      </td></tr>
                    ) : jitRequests.map(req => (
                      <tr key={req.id} className="hover:bg-slate-50 transition-colors" data-testid={`jit-history-${req.id}`}>
                        <td className="px-4 py-3"><p className="font-medium text-navy-900">{req.requester_name}</p><p className="text-xs text-navy-400">{req.requester_email}</p></td>
                        <td className="px-4 py-3 font-medium text-navy-700">{req.role_name}</td>
                        <td className="px-4 py-3 text-navy-600">{req.duration_hours}h</td>
                        <td className="px-4 py-3"><Badge className={`text-[10px] uppercase ${STATUS_STYLES[req.status] || 'bg-slate-100 text-navy-600'}`}>{req.status}</Badge></td>
                        <td className="px-4 py-3 text-navy-500 text-xs">{new Date(req.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3 text-xs text-navy-500">{req.approved_by_name || req.rejected_by_name || req.revoked_by_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
